import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'src/hooks';
import Button from '@mui/material/Button';
import { Alert, CircularProgress, Grid, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Close } from '@mui/icons-material';
import DialogTitle from '@mui/material/DialogTitle';
import TableComponent from 'src/components/collapsible-table/table';
import {
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  RowSelectionState
} from '@tanstack/table-core';
import { useReactTable } from '@tanstack/react-table';
import TableSkelatonContainer from '../pricing-simulator/components/simulation-table-container/table-skeleton';
import ScenarioManagerTopBar from '../scenario-manager/scenario-manager-top-bar';
import { getFilteredRows } from '../scenario-manager/scenario-manager-util';
import { useAppDispatch } from 'src/hooks';
import { resetSelectedScenario } from '../pricing-simulator/store/pricing-simulator-slice';

interface IScenarioListProps {
  title: string;
  fetchingTableData: boolean;
  tableRows: any;
  tableColumns: any;
  open: boolean;
  footerBtnName: string;
  isMultiple: boolean;
  // eslint-disable-next-line react/require-default-props
  selectionLimit?: number;
  // eslint-disable-next-line react/require-default-props
  preSelectedScenarioIds?: number[];
  onClose: (args: any) => void;
  onScenarioLoad: (args: { scenario_id: number }[]) => Promise<unknown>;
}

export default function ScenarioListDialog({
  title,
  fetchingTableData,
  tableRows,
  tableColumns,
  open,
  footerBtnName,
  isMultiple,
  selectionLimit = 0,
  preSelectedScenarioIds = [],
  onClose,
  onScenarioLoad
}: IScenarioListProps) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const tableRef = useRef<HTMLTableElement>();
  const { activeCheckboxValues, searchInputVal } = useAppSelector(
    (state) => state.scenarioManagerTopBar
  );

  const appDispatch = useAppDispatch();

  const [fetchingScenarios, setFetchingScenario] = useState(false);

  const [rows, setRows] = useState(
    !fetchingTableData
      ? getFilteredRows(tableRows, searchInputVal, activeCheckboxValues)
      : []
  );

  useEffect(() => {
    if (!fetchingTableData) {
      setRows(getFilteredRows(tableRows, searchInputVal, activeCheckboxValues));
    }
  }, [activeCheckboxValues, searchInputVal, fetchingTableData]);

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    state: {
      expanded,
      rowSelection
    },
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    getSubRows: (row: any) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel()
  });

  const handleClose = () => {
    const selectedRowDetails = table
      .getSelectedRowModel()
      .flatRows.map((row) => row.original);
    setFetchingScenario(true);
    onScenarioLoad(selectedRowDetails).then(() => {
      setFetchingScenario(false);
      onClose(null);
    });
  };

  useEffect(() => {
    if (preSelectedScenarioIds.length) {
      const defaultRowSelection = table
        .getRowModel()
        .flatRows.filter((row) =>
          preSelectedScenarioIds.includes(row.original.scenario_id)
        )
        .map((row) => row.id)
        .reduce<RowSelectionState>(
          (prev, curr) => ({ ...prev, [curr]: true }),
          {}
        );
      setRowSelection(defaultRowSelection);
    }
  }, []);

  const isApplyButtonDisabled = () =>
    fetchingScenarios ||
    !Object.values(rowSelection).filter((value) => value).length ||
    (selectionLimit &&
      Object.values(rowSelection).filter((value) => value).length >
        selectionLimit);

  return (
    <div>
      <Dialog
        open={open}
        // onClose={() => onClose(null)}
        sx={{ '.MuiDialog-paper': { width: '100%' } }}
        maxWidth='xl'
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          <Grid container justifyContent='space-between'>
            <Grid item xs='auto' sx={{ paddingTop: '10px', fontSize: '18px' }}>
              {title}
            </Grid>
            <Grid item xs='auto'>
              <IconButton
                onClick={() => {
                  onClose(null);
                  appDispatch(resetSelectedScenario());
                }}
              >
                <Close />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <ScenarioManagerTopBar isNewScenarioBtnPresent={false} />
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                {(fetchingTableData || !tableColumns.length) && (
                  <Grid item xs={12}>
                    <TableSkelatonContainer rowLength={10} headerLength={6} />
                  </Grid>
                )}
                {!fetchingTableData && (
                  <Grid item xs={12}>
                    <TableComponent
                      setHeightExplicitly={false}
                      table={table}
                      extClassName='scenario-manager-table duplicate-scenario-table'
                      tableRef={tableRef}
                      isRowSelect
                      isMultiple={isMultiple}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent='end'>
            <Grid item xs={12} mb={1}>
              {selectionLimit &&
              Object.values(rowSelection).filter((value) => value).length >
                selectionLimit ? (
                <Alert severity='error'>
                  Max {selectionLimit} rows can be selected
                </Alert>
              ) : null}
            </Grid>
            <Button
              disabled={isApplyButtonDisabled()}
              color='primary'
              variant='outlined'
              onClick={handleClose}
              autoFocus
            >
              {' '}
              {fetchingScenarios && (
                <CircularProgress
                  style={{
                    width: ' 15px',
                    height: '15px',
                    marginRight: '8px'
                  }}
                />
              )}
              {footerBtnName}
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
}
